import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Select,
  MenuItem,
  Alert,
  Grid,
  Checkbox
} from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
// import RestoreIcon from '@mui/icons-material/Restore'; // Unused for now
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ReferenceLine } from 'recharts';
import { Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material';

const DIFC_COLORS = {
  primary: '#003366',
  secondary: '#00796B',
  accent: '#FFC72C',
  background: '#F5F8FA',
  textPrimary: '#1E2D3B',
  textSecondary: '#4A6B82',
};

// Updated color palette
const CUSTOM_PALETTE = {
  "Grey": "#B0BEC5",         // Soft Cool Grey
  "Light_Orange": "#FFCC80", // Pastel Orange
  "Light_Pink": "#F8BBD0",   // Soft Pink
  "Light_Blue": "#8FA8CC",   // Darker blue (darker version of RGB 201,214,235)
  "Light_Green": "#5EBA9D",  // Updated green: RGB 94,186,157
};

// Updated chart colors array using the custom palette
const CHART_COLORS = [
  CUSTOM_PALETTE.Light_Blue,   // 1 Month
  CUSTOM_PALETTE.Light_Green,  // 2 Months
  CUSTOM_PALETTE.Light_Orange, // 3 Months
  CUSTOM_PALETTE.Light_Pink,   // 6 Months
  CUSTOM_PALETTE.Grey          // 12 Months
];

function SubscriptionOptimization({ selectedArea }) {
  const [activeButton, setActiveButton] = useState(null); // 'yearly' or 'update'

  // Helper function to get max capacity based on selected area
  const getMaxCapacity = () => {
    return selectedArea === 'gate_village' ? 1600 : 2009;
  };

  const [rows, setRows] = useState([
    { id: 1, subscribers: 0, months: 1 }
  ]);
  const [loadingButton, setLoadingButton] = useState(null); // 'yearly' or 'update'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previousOptimizationResults, setPreviousOptimizationResults] = useState(null);
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [enableTargetSelling, setEnableTargetSelling] = useState(false);
  const [targetAmount, setTargetAmount] = useState(0);
  const [targetSubscriptionLength, setTargetSubscriptionLength] = useState(1);
  const monthOptions = [
    { value: 1, label: '1 month', index: '1' },
    { value: 2, label: '2 months', index: '2' },
    { value: 3, label: '3 months', index: '3' },
    { value: 6, label: '6 months', index: '4' },
    { value: 12, label: '12 months', index: '5' }
  ];
  const [salesEntries, setSalesEntries] = useState([
  { id: Date.now(), monthLength: 1, sold: 0 }
]);

  // Helper function to generate progressive rolling window
  const generateProgressiveWindow = (currentDate = new Date(2025, 5)) => {
    // Starting point: June 2025
    const startDate = new Date(2025, 5); // June 2025 (month 5 = June in 0-based indexing)
    const months = {};
    
    // Calculate how many months have passed since June 2025
    const monthsPassed = (currentDate.getFullYear() - startDate.getFullYear()) * 12 + 
                        (currentDate.getMonth() - startDate.getMonth()) + 1; // +1 to include current month
    
    // Determine the window size and starting point
    let windowSize, startOffset;
    
    if (monthsPassed <= 12) {
      // Progressive phase: show from June 2025 up to current month
      windowSize = monthsPassed;
      startOffset = 0;
    } else {
      // Rolling phase: maintain 12-month window
      windowSize = 12;
      startOffset = monthsPassed - 12;
    }
    
    // Generate the window
    for (let i = 0; i < windowSize; i++) {
      const date = new Date(startDate.getFullYear(), startDate.getMonth() + startOffset + i);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months[key] = [{
        id: Date.now() + i,
        monthLength: 1,
        sold: 0
      }];
    }
    
    return months;
  };

  // Function to simulate time progression (for demonstration)
  // const simulateTimeProgression = (targetMonth, targetYear) => { // Unused
  //   const newDate = new Date(targetYear, targetMonth - 1); // Convert to 0-based month
  //   setMonthlySales(generateProgressiveWindow(newDate));
  // };

  // Function to restore previous optimization plan
  const restorePreviousPlan = async () => {
  setEnableTargetSelling(false);
  setTargetAmount(0);
  setError(null);
  setLoading(true);
  
  try {
    // Run optimization based on current saved sales data (without target selling)
    const soldDict = {};
    for (let i = 1; i <= 12; i++) {
      soldDict[i] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    }
    
    // Fill in user-provided sales from the update window
    Object.entries(monthlySales).forEach(([monthKey, entries]) => {
      const monthIndex = getMonthIndex(monthKey);
      if (monthIndex >= 1 && monthIndex <= 12) {
        entries.forEach(entry => {
          const tierIndex = monthOptions.find(opt => opt.value === Number(entry.monthLength))?.index || 5;
          soldDict[monthIndex][tierIndex] = Number(entry.sold);
        });
      }
    });

    const requestData = {
      query: {}, // Empty query for basic optimization
      sold: soldDict,
      occupancy: predictions || {},
      area: selectedArea,
      parameters: {
        basePrice: 1000,
        maxCapacity: getMaxCapacity()
      }
    };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/parse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const data = await response.json();
    if (data.status === 'success') {
      setOptimizationResults({
        optimized_occupancy: data.optimized_occupancy,
        subscription_details: data.subscription_details,
        revenue: data.revenue,
      });
    } else {
      // Fallback to previous results if API call fails
      if (previousOptimizationResults) {
        setOptimizationResults(previousOptimizationResults);
      }
    }
  } catch (error) {
    console.error('Error restoring plan:', error);
    // Fallback to previous results if API call fails
    if (previousOptimizationResults) {
      setOptimizationResults(previousOptimizationResults);
    }
  } finally {
    setLoading(false);
  }
};


  // Function to reset all sales data to 0
  const resetAllSalesData = () => {
    const resetSales = {};
    // Reset all entries to 0 while maintaining the structure
    Object.entries(monthlySales).forEach(([monthKey, entries]) => {
      resetSales[monthKey] = entries.map(entry => ({
        ...entry,
        sold: 0
      }));
    });
    setMonthlySales(resetSales);
    saveSalesData(resetSales); // Auto-save the reset state
  };

  // Replace the existing useEffect for monthlySales initialization
  const [monthlySales, setMonthlySales] = useState({});
  const [salesLoadedArea, setSalesLoadedArea] = useState(null);
  const [isLoadingSalesData, setIsLoadingSalesData] = useState(true);

  const loadSavedSalesData = async () => {
    setIsLoadingSalesData(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sales-data?area=${selectedArea}`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success' && data.salesData) {
          setMonthlySales(data.salesData);
          setSalesLoadedArea(selectedArea);
          return data.salesData;
        } else {
          const fresh = generateProgressiveWindow();
          setMonthlySales(fresh);
          setSalesLoadedArea(selectedArea);
          return fresh;
        }
      } else {
        const fresh = generateProgressiveWindow();
        setMonthlySales(fresh);
        setSalesLoadedArea(selectedArea);
        return fresh;
      }
    } catch (error) {
      console.error('Error loading saved sales data:', error);
      const fresh = generateProgressiveWindow();
      setMonthlySales(fresh);
      setSalesLoadedArea(selectedArea);
      return fresh;
    } finally {
      setIsLoadingSalesData(false);
    }
  };

  const saveSalesData = async (salesData) => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/sales-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ salesData, area: selectedArea })
      });
    } catch (error) {
      console.error('Error saving sales data:', error);
    }
  };

  // Add after other useState declarations
  // const [currentMonthSales, setCurrentMonthSales] = useState({ // Unused
  //   1: 0, // 1 month
  //   2: 0, // 2 months
  //   3: 0, // 3 months
  //   4: 0, // 6 months
  //   5: 0  // 12 months
  // });
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);

  // const handleAddSalesEntry = () => { // Unused
  //   setSalesEntries([...salesEntries, { id: Date.now(), monthLength: 2, sold: 0 }]);
  // };

  // const handleRemoveSalesEntry = (id) => { // Unused
  //   setSalesEntries(salesEntries.filter(entry => entry.id !== id));
  // };

  // const handleSalesEntryChange = (id, field, value) => { // Unused
  //   setSalesEntries(salesEntries.map(entry =>
  //     entry.id === id ? { ...entry, [field]: value } : entry
  //   ));
  // };

  const handleAddMonthlyEntry = (monthKey) => {
    const updatedSales = {
      ...monthlySales,
      [monthKey]: [
        ...monthlySales[monthKey],
        { id: Date.now(), monthLength: 1, sold: 0 }
      ]
    };
    setMonthlySales(updatedSales);
    saveSalesData(updatedSales); // Auto-save
  };

  const handleRemoveMonthlyEntry = (monthKey, id) => {
    const updatedSales = {
      ...monthlySales,
      [monthKey]: monthlySales[monthKey].filter(entry => entry.id !== id)
    };
    setMonthlySales(updatedSales);
    saveSalesData(updatedSales); // Auto-save
  };

  const handleMonthlyEntryChange = (monthKey, id, field, value) => {
    const updatedSales = {
      ...monthlySales,
      [monthKey]: monthlySales[monthKey].map(entry =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    };
    setMonthlySales(updatedSales);
    // Debounce auto-save for input changes
    clearTimeout(window.salesSaveTimeout);
    window.salesSaveTimeout = setTimeout(() => {
      saveSalesData(updatedSales);
    }, 1000); // Save 1 second after user stops typing
  };

  // Helper function to format month display
  const formatMonthDisplay = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  // Helper function to get month index for backend (1-12)
  const getMonthIndex = (monthKey, currentDate = new Date(2025, 5)) => {
    const [year, month] = monthKey.split('-');
    const startDate = new Date(2025, 5); // June 2025
    
    const targetYear = parseInt(year);
    const targetMonth = parseInt(month);
    
    // Calculate months from start date
    const monthsFromStart = (targetYear - startDate.getFullYear()) * 12 + 
                           (targetMonth - 1 - startDate.getMonth());
    
    // Calculate months passed since start
    const monthsPassed = (currentDate.getFullYear() - startDate.getFullYear()) * 12 + 
                        (currentDate.getMonth() - startDate.getMonth()) + 1;
    
    if (monthsPassed <= 12) {
      // Progressive phase: map directly to 1-based index
      return monthsFromStart + 1;
    } else {
      // Rolling phase: map to 1-12 range
      const windowStart = monthsPassed - 12;
      return monthsFromStart - windowStart + 1;
    }
  };

  const fetchPredictions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/predictions?area=${selectedArea}`);
      const data = await response.json();
      if (data.status === 'success') {
        setPredictions(data.predictions);
        return data.predictions;
      }
      return null;
    } catch (error) {
      console.error('Error fetching predictions:', error);
      return null;
    }
  };

  // Load saved sales data on component mount
  useEffect(() => {
    (async () => {
      const [salesData, preds] = await Promise.all([
        loadSavedSalesData(),
        fetchPredictions()
      ]);
      if (salesData && preds) {
        await handleOptimize({ salesDataOverride: salesData, predictionsOverride: preds });
      }
    })();
  }, []);

  // Reload data when area changes
  useEffect(() => {
    setOptimizationResults(null);
    setError(null);
    setMonthlySales({});
    setSalesLoadedArea(null);
    (async () => {
      const [salesData, preds] = await Promise.all([
        loadSavedSalesData(),
        fetchPredictions()
      ]);
      if (salesData && preds) {
        await handleOptimize({ salesDataOverride: salesData, predictionsOverride: preds });
      }
    })();
  }, [selectedArea]);

  // Removed secondary auto-optimize effect to avoid duplicate runs

  // const handleAddRow = () => { // Unused
  //   const newRow = {
  //     id: Date.now(),
  //     subscribers: 0,
  //     months: 1
  //   };
  //   setRows([...rows, newRow]);
  // };

  // const handleRemoveRow = (id) => { // Unused
  //   if (rows.length > 1) {
  //     setRows(rows.filter(row => row.id !== id));
  //   }
  // };

  // const handleChange = (id, field, value) => { // Unused
  //   setRows(rows.map(row => {
  //     if (row.id === id) {
  //       return { ...row, [field]: value };
  //     }
  //     return row;
  //   }));
  // };

  // Updated formatSubscriptionData function to show 12 months starting from current month
  const formatSubscriptionData = (details) => {
    console.log('Raw subscription details from backend:', details);
    
    // Get current month and year
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-based (0=Jan, 5=Jun, etc.)
    const currentYear = currentDate.getFullYear();
    
    // Generate 12 months starting from current month
    const monthlyData = {};
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Initialize data for 12 months starting from current month
    for (let i = 0; i < 12; i++) {
      const monthIndex = (currentMonth + i) % 12;
      // const year = currentYear + Math.floor((currentMonth + i) / 12); // Unused
      const monthKey = monthNames[monthIndex];
      
      monthlyData[monthKey] = {
        month: monthKey,
        '1 Month': 0,
        '2 Months': 0,
        '3 Months': 0,
        '6 Months': 0,
        '12 Months': 0
      };
    }
    
    // Fill in actual data where available
    details.forEach(item => {
      console.log('Processing item:', item);
      
      // Convert month to first 3 letters
      const monthAbbr = item.month ? item.month.substring(0, 3) : item.month;
      console.log(`Month abbreviation: ${monthAbbr}, Length: ${item.length}, Value: ${item.value}`);
      
      if (monthlyData[monthAbbr]) {
        const key = `${item.length} Month${item.length === 1 ? '' : 's'}`;
        console.log(`Setting ${monthAbbr}[${key}] = ${item.value}`);
        monthlyData[monthAbbr][key] = item.value;
      } else {
        console.warn(`Month ${monthAbbr} not found in monthlyData keys:`, Object.keys(monthlyData));
      }
    });
    
    // Return months in the order they appear (current month first)
    const result = [];
    for (let i = 0; i < 12; i++) {
      const monthIndex = (currentMonth + i) % 12;
      const monthKey = monthNames[monthIndex];
      result.push(monthlyData[monthKey]);
    }
    
    console.log('Final formatted data for chart:', result);
    return result;
  };

  // Helper function to get consistent color for subscription length
  const getColorForSubscriptionLength = (length) => {
    const colorMap = {
      1: CUSTOM_PALETTE.Light_Blue,   // 1 Month
      2: CUSTOM_PALETTE.Light_Green,  // 2 Months
      3: CUSTOM_PALETTE.Light_Orange, // 3 Months
      6: CUSTOM_PALETTE.Light_Pink,   // 6 Months
      12: CUSTOM_PALETTE.Grey         // 12 Months
    };
    return colorMap[length] || CUSTOM_PALETTE.Grey;
  };

  const formatPieData = (details) => {
    const currentMonth = new Date().toLocaleString('default', { month: 'long' });
    return details
      .filter(item => item.month === currentMonth)
      .map(item => ({
        name: `${item.length} Months`,
        value: item.value,
        length: item.length // Add length for consistent coloring
      }));
  };

  const formatOccupancyData = (optimizedOccupancy, predictions) => {
  if (!optimizedOccupancy || !predictions) return [];
  
  // Get current month (0-based: 0=Jan, 5=Jun, etc.)
  const currentMonth = new Date().getMonth();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  
  // Create array for next 12 months starting from current month
  const result = [];
  
  for (let i = 0; i < 12; i++) {
    // Calculate the actual month index (0-11) for display
    const monthIndex = (currentMonth + i) % 12;
    const monthName = monthNames[monthIndex];
    
    // Predictions: key "1" = current month, "2" = next month, etc.
    const predictionKey = (i + 1).toString();
    const predictionData = predictions[predictionKey];
    
    // Optimized occupancy: index 12 = current month, 13 = next month, etc.
    const optimizedKey = (12 + i).toString();
    const optimizedValue = optimizedOccupancy[optimizedKey] || 0;
    
    // Convert prediction value to actual occupancy count
    const currentOccupancy = predictionData ? predictionData.value : 0;
    
    result.push({
      month: monthName,
      'Current Occupancy': currentOccupancy,
      'Optimized Occupancy': optimizedValue,
      'Max Capacity': getMaxCapacity()
    });
  }
  
  return result;
};
  // const [sold, setSold] = useState({ // Unused
  //   11: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, // Current month
  //   12: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, // June
  //   13: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, // July
  //   14: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }  // August
  // });
  
  // const handleSoldChange = (month, tier, value) => { // Unused
  //   setSold(prev => ({
  //     ...prev,
  //     [month]: {
  //       ...prev[month],
  //       [tier]: value
  //     }
  //   }));
  // };

// Add after other function declarations
const handleUpdateSold = async () => {
  setActiveButton('update');
  setLoadingButton('update');

  setLoading(true);
  setError(null);
  try {
    // Build sold dictionary for backend (all months, all tiers, default 0)
    const soldDict = {};
    for (let i = 1; i <= 12; i++) {
      soldDict[i] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    }
    
    // Fill in user-provided sales
    Object.entries(monthlySales).forEach(([monthKey, entries]) => {
      const monthIndex = getMonthIndex(monthKey);
      if (monthIndex >= 1 && monthIndex <= 12) {
        entries.forEach(entry => {
          const tierIndex = monthOptions.find(opt => opt.value === Number(entry.monthLength))?.index || 5;
          soldDict[monthIndex][tierIndex] = Number(entry.sold);
        });
      }
    });

     // Prepare request data
    const requestData = {
      query: {},
      sold: soldDict,
      occupancy: predictions || {},
      area: selectedArea,
      parameters: {
        basePrice: 1000,
        maxCapacity: getMaxCapacity(),
        mode: 'cap_line'
      }
    };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/parse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    const data = await response.json();
    if (data.status === 'success') {
      setOptimizationResults({
        optimized_occupancy: data.optimized_occupancy,
        subscription_details: data.subscription_details,
        revenue: data.revenue,
        // predicted_occupancy: data.predicted_occupancy
      });
    } else if (data.status === 'infeasible') {
      // Show specific message based on optimization stage
      if (data.stage === 'parking_optimization') {
        setError('This amount is not applicable to sell as it would cause exceeding parking capacity.');
      } else {
        setError('Optimization is not applicable for the current input (problem is infeasible).');
      }
      setOptimizationResults(null);
    } else {
      throw new Error(data.message || 'Update failed');
    }
  } catch (error) {
    setError(`Update failed: ${error.message}`);
    console.error('Update error:', error);
  } finally {
    setLoading(false);
    setLoadingButton(null);

  }
};

  const checkFeasibility = async () => {
    setLoading(true);
    setError(null);
    
    // Store current optimization results before checking feasibility
    if (optimizationResults) {
      setPreviousOptimizationResults(optimizationResults);
    }
    
    try {
      // Build sold dictionary from monthlySales (same logic as handleOptimize and handleUpdateSold)
      const soldDict = {};
      for (let i = 1; i <= 12; i++) {
        soldDict[i] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      }
      
      // Fill in user-provided sales from the update window
      Object.entries(monthlySales).forEach(([monthKey, entries]) => {
        const monthIndex = getMonthIndex(monthKey);
        if (monthIndex >= 1 && monthIndex <= 12) {
          entries.forEach(entry => {
            const tierIndex = monthOptions.find(opt => opt.value === Number(entry.monthLength))?.index || 5;
            soldDict[monthIndex][tierIndex] = Number(entry.sold);
          });
        }
      });

      const requestData = {
        query: {
          target: {
            amount: targetAmount,
            months: targetSubscriptionLength
          }
        },
        sold: soldDict,  // Use sales data from the update window
        occupancy: predictions || {},
        area: selectedArea,
        parameters: {
          basePrice: 1000,
          maxCapacity: getMaxCapacity(),
          mode: 'cap_line'
        }
      };
  
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/parse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });
  
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
  
      const data = await response.json();
      if (data.status === 'success') {
        setOptimizationResults({
        optimized_occupancy: data.optimized_occupancy,
        subscription_details: data.subscription_details,
        revenue: data.revenue,
        // predicted_occupancy: data.predicted_occupancy
      });
      } else if (data.status === 'infeasible') {
        // Show specific message based on optimization stage
        if (data.stage === 'parking_optimization') {
          setError('This amount is not applicable to sell as it would cause exceeding parking capacity.');
        } else {
          setError('Optimization is not applicable for the current input (problem is infeasible).');
        }
        setOptimizationResults(null);
      } else {
        throw new Error(data.message || 'Feasibility check failed');
      }
    } catch (error) {
      setError(`Feasibility check failed: ${error.message}`);
      console.error('Feasibility error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptimize = async (opts = {}) => {
    const { salesDataOverride = null, predictionsOverride = null } = opts;
    setActiveButton('yearly');
    setEnableTargetSelling(false); // Uncheck the checkbox by default
    setLoadingButton('yearly');

    setLoading(true);
    setError(null);
    try {
      const localMonthlySales = salesDataOverride || monthlySales;
      const localPredictions = predictionsOverride || predictions;

      // Convert rows to query object, only including non-zero values
      const query = rows.reduce((acc, row) => {
        const option = monthOptions.find(opt => opt.value === row.months);
        if (option && row.subscribers > 0) {  // Only include non-zero values
          acc[option.index] = Number(row.subscribers);
        }
        return acc;
      }, {});

      // Build sold dictionary from localMonthlySales
      const soldDict = {};
      for (let i = 1; i <= 12; i++) {
        soldDict[i] = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      }
      Object.entries(localMonthlySales || {}).forEach(([monthKey, entries]) => {
        const monthIndex = getMonthIndex(monthKey);
        if (monthIndex >= 1 && monthIndex <= 12) {
          entries.forEach(entry => {
            const tierIndex = monthOptions.find(opt => opt.value === Number(entry.monthLength))?.index || 5;
            soldDict[monthIndex][tierIndex] = Number(entry.sold);
          });
        }
      });

      // Prepare request data with all required inputs
      const requestData = {
        query: Object.keys(query).length > 0 ? query : {},  // Empty object if no valid inputs
        sold: soldDict,  // Use sales data from local source
        occupancy: localPredictions || {},  // Use predictions from local source
        area: selectedArea,
        parameters: {
          basePrice: 1000,  // Default base price
          maxCapacity: getMaxCapacity(), // Maximum parking capacity
          mode: 'cap_line'
        }
      };
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/parse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        // Clear any lingering error if we have a successful response
        setError(null);
        setOptimizationResults({
          optimized_occupancy: data.optimized_occupancy,
          subscription_details: data.subscription_details,
          revenue: data.revenue,
        });
      } else if (data.status === 'infeasible') {
        if (data.stage === 'parking_optimization') {
          setError('This amount is not applicable to sell as it would cause exceeding parking capacity.');
        } else {
          setError('Optimization is not applicable for the current input (problem is infeasible).');
        }
        setOptimizationResults(null);
      } else {
        throw new Error(data.message || 'Optimization failed');
      }
    } catch (error) {
      setError(`Optimization failed: ${error.message}`);
      console.error('Optimization error:', error);
    } finally {
      setLoading(false);
      setLoadingButton(null);
    }
  };

  // Remove secondary auto-optimize to avoid different code paths; rely on area-change flow above
  // ...other code...

  // Add this block just before the return (
  let avgCurrent = 0;
  let avgOptimized = 0;
  let occupancyGain = 0;
  if (optimizationResults && predictions) {
    const occData = formatOccupancyData(optimizationResults.optimized_occupancy, predictions);
    const currentSum = occData.reduce((sum, d) => sum + d['Current Occupancy'], 0);
    const optimizedSum = occData.reduce((sum, d) => sum + d['Optimized Occupancy'], 0);
    avgCurrent = currentSum / occData.length;
    avgOptimized = optimizedSum / occData.length;
    
    // Calculate percentage point difference instead of percentage change
    const currentPercentage = (avgCurrent / getMaxCapacity()) * 100;
    const optimizedPercentage = (avgOptimized / getMaxCapacity()) * 100;
    occupancyGain = optimizedPercentage - currentPercentage; // This gives percentage points
  }
  return (
    <Box sx={{ p: 2 }}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, maxWidth: '1200px', mx: 'auto' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <QueryStatsIcon sx={{ fontSize: 32, color: DIFC_COLORS.primary, mb: 1 }} />
          <Typography variant="h5" sx={{
            fontFamily: 'Lufga',
            fontWeight: 600,
            color: DIFC_COLORS.textPrimary,
            mb: 0.5,
            fontSize: '1.3rem'
          }}>
            Subscription Optimization
          </Typography>
        </Box>


        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          {rows.map((row) => (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mb: 2, alignItems: 'center', justifyContent: 'center' }}>
          <TextField
            label="Active Subscribers"
            type="number"
            value={getMaxCapacity() === 1600 ? 1400 : 2000}
            disabled
            sx={{
              width: '180px',
              '& .Mui-disabled': {
                WebkitTextFillColor: '#000000',
                color: '#000000'
              }
            }}
          />
          <TextField
            label="Base Price (AED)"
            type="number"
            value={1000}
            disabled
            sx={{
              width: '180px',
              '& .Mui-disabled': {
                WebkitTextFillColor: '#000000',
                color: '#000000'
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleOptimize}
            disabled={loading}
            sx={{
              fontFamily: 'Lufga',
              fontWeight: 500,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '12px',
              borderRadius: '4px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#374151',
              }
            }}
          >
            {loading && loadingButton === 'yearly' ? <CircularProgress size={24} /> : 'Optimize Subscriptions'}
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenUpdateDialog(true)}
            sx={{
              fontFamily: 'Lufga',
              fontWeight: 500,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '12px',
              borderRadius: '4px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#374151',
              }
            }}
          >
            {loading && loadingButton === 'update' ? <CircularProgress size={24} /> : 'Sales Activity Tracker'}
          </Button>
        </Box>
          ))}

          {/* Update Sales Dialog - Modified */}
          <Dialog 
              open={openUpdateDialog}
              onClose={() => setOpenUpdateDialog(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle
                sx={{
                  textAlign: 'center',
                  fontFamily: 'Lufga',
                  color: DIFC_COLORS.textPrimary
                }}
              >
                Sales Activity Tracker (Progressive Window)
              </DialogTitle>
              <DialogContent>
                {isLoadingSalesData ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Loading saved data...</Typography>
                  </Box>
                ) : (
                  <>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    color: DIFC_COLORS.textSecondary,
                    fontFamily: 'Lufga'
                  }}
                >
                Enter how many new subscribers you've added each month until now, and select their subscription duration. This updates your annual plan accordingly.                </Typography>

                {/* Reset Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetAllSalesData}
                    sx={{
                      color: '#d32f2f',
                      borderColor: '#d32f2f',
                      fontFamily: 'Lufga',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 0.04)',
                        borderColor: '#b71c1c'
                      }
                    }}
                  >
                    Reset All to 0
                  </Button>
                </Box>

                {/* Demo buttons to show progression - Remove in production */}
                
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Month & Year</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Subscription Length</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Number Sold</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(monthlySales)
                      .sort(([a], [b]) => a.localeCompare(b)) // Sort by month key
                      .map(([monthKey, entries]) =>
                      entries.map((entry, idx) => (
                        <TableRow key={entry.id}>
                          {idx === 0 && (
                            <TableCell rowSpan={entries.length} sx={{ 
                              fontWeight: 'medium',
                              bgcolor: monthKey === '2025-06' ? '#e3f2fd' : 'inherit' // Highlight current month
                            }}>
                              {formatMonthDisplay(monthKey)}

                            </TableCell>
                          )}
                          <TableCell>
                            <Select
                              value={entry.monthLength}
                              onChange={e => handleMonthlyEntryChange(monthKey, entry.id, 'monthLength', Number(e.target.value))}
                              sx={{ width: 140 }}
                              size="small"
                            >
                              {monthOptions.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                              ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={entry.sold}
                              onChange={e => handleMonthlyEntryChange(monthKey, entry.id, 'sold', e.target.value)}
                              sx={{ width: 100 }}
                              size="small"
                              InputProps={{ inputProps: { min: 0 } }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleRemoveMonthlyEntry(monthKey, entry.id)}
                              disabled={entries.length === 1}
                              size="small"
                            >
                              <RemoveCircleOutlineIcon />
                            </IconButton>
                            {idx === entries.length - 1 && (
                              <IconButton 
                                onClick={() => handleAddMonthlyEntry(monthKey)}
                                size="small"
                              >
                                <AddCircleOutlineIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                </>
                )}
          </DialogContent>
            <DialogActions sx={{ p: 3 }}>
              <Button
                onClick={() => setOpenUpdateDialog(false)}
                sx={{
                  color: DIFC_COLORS.textSecondary,
                  fontFamily: 'Lufga'
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  handleUpdateSold();
                  setOpenUpdateDialog(false);
                }}
                disabled={loading}
                sx={{
              fontFamily: 'Lufga',
              fontWeight: 500,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '12px',
              borderRadius: '4px',
              mb: 2,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#374151',
              }
            }}
              >
                {loading ? <CircularProgress size={24} /> : 'Update Sales'}
              </Button>
            </DialogActions>
          </Dialog>

          <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 2, 
          mt: 2 
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1 
          }}>
            <Checkbox
              checked={enableTargetSelling}
              onChange={(e) => setEnableTargetSelling(e.target.checked)}
              sx={{ color: DIFC_COLORS.primary }}
            />
            <Typography>Test Sales Request (Current Month)</Typography>
          </Box>

          {enableTargetSelling && (
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center' 
            }}>
              <TextField
                label="Target Amount"
                type="number"
                value={targetAmount}
                onChange={(e) => setTargetAmount(Number(e.target.value))}
                sx={{ width: '180px' }}
                InputProps={{ inputProps: { min: 0 } }}
              />
              <Select
                value={targetSubscriptionLength}
                onChange={(e) => setTargetSubscriptionLength(e.target.value)}
                sx={{ width: '180px' }}
              >
                {monthOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <Button
                variant="contained"
                onClick={checkFeasibility}
                disabled={loading}
                sx={{
              fontFamily: 'Lufga',
              fontWeight: 500,
              backgroundColor: '#1f2937',
              color: 'white',
              padding: '12px',
              borderRadius: '4px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#374151',
              }
            }}
              >
                {loading ? <CircularProgress size={24} /> : 'Check Feasibility'}
              </Button>
            </Box>
          )}
        </Box>
          
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mt: 2 }}
            action={
              // Show restore button only for parking capacity errors and when target selling is enabled
              error.includes('exceeding parking capacity') && enableTargetSelling ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={restorePreviousPlan}
                  sx={{
                    color: '#d32f2f',
                    borderColor: '#d32f2f',
                    fontFamily: 'Lufga',
                    textTransform: 'none',
                    fontSize: '0.875rem',
                    padding: '4px 12px',
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.04)',
                      borderColor: '#b71c1c'
                    }
                  }}
                >
                  Restore Previous Plan
                </Button>
              ) : null
            }
          >
            {error}
          </Alert>
        )}

        {optimizationResults && !loading && (
          <>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3 }}>
                  
                  <Typography variant="h6" align="center" sx={{ mb: 2, color: DIFC_COLORS.primary }}>
                    {new Date().toLocaleString('default', { month: 'long' })} Sales Distribution
                  </Typography>
                  <Typography variant="subtitle1" align="center" sx={{ mb: 2, color: DIFC_COLORS.textSecondary }}>
                    Subscription Tier Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={formatPieData(optimizationResults.subscription_details)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        dataKey="value"
                        label={({ name, value, percent }) => {
        // Custom label function that shows name and percentage
        return `${name}: ${value}`;
      }}
                      >
                        {formatPieData(optimizationResults.subscription_details).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={getColorForSubscriptionLength(entry.length)} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" align="center" sx={{ mb: 2, color: DIFC_COLORS.primary }}>
                    Revenue Optimization
                  </Typography>
                  <Typography variant="subtitle1" align="center" sx={{ mb: 2, color: DIFC_COLORS.textSecondary }}>
                    Monthly Revenue Timeline
                  </Typography>
                  

                  
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={formatSubscriptionData(optimizationResults.subscription_details)}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month" 
                        interval={0}
                        angle={0}
                        textAnchor="middle"
                        height={40}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => {
                          // Show actual values in tooltip, including zeros
                          return [value || 0, name];
                        }}
                        labelFormatter={(label) => `Month: ${label}`}
                      />
                      <Legend />
                      <Bar 
                        dataKey="1 Month" 
                        stackId="a" 
                        fill={CHART_COLORS[0]}
                        minPointSize={1}
                      />
                      <Bar 
                        dataKey="2 Months" 
                        stackId="a" 
                        fill={CHART_COLORS[1]}
                        minPointSize={1}
                      />
                      <Bar 
                        dataKey="3 Months" 
                        stackId="a" 
                        fill={CHART_COLORS[2]}
                        minPointSize={1}
                      />
                      <Bar 
                        dataKey="6 Months" 
                        stackId="a" 
                        fill={CHART_COLORS[3]}
                        minPointSize={1}
                      />
                      <Bar 
                        dataKey="12 Months" 
                        stackId="a" 
                        fill={CHART_COLORS[4]}
                        minPointSize={1}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center',
                  mb: 2 
                }}>
                  
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 3 }}>
                  <Typography variant="h6" align="center" sx={{ mb: 2, color: DIFC_COLORS.primary }}>
                    Occupancy Comparison
                  </Typography>
                  <Typography variant="subtitle1" align="center" sx={{ mb: 2, color: DIFC_COLORS.textSecondary }}>
                    Current vs Optimized Occupancy
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={formatOccupancyData(optimizationResults.optimized_occupancy, predictions)}
                      margin={{ top: 20, right: 30, left: 20, bottom: 25 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="month"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={0}
                      />
                      <YAxis 
                        domain={[selectedArea === 'gate_village' ? 800 : 1300, getMaxCapacity()]}
                        ticks={selectedArea === 'gate_village' ? [800, 1000, 1200, 1400, 1600] : [1300, 1450, 1600, 1750, 1900, 2009]}
                        // label={{ 
                        //   value: 'Occupancy', 
                        //   angle: -90, 
                        //   position: 'insideLeft',
                        //   offset: -50
                        // }}
                      />
                      <Tooltip 
                        formatter={(value, name) => [Math.round(value), name]}
                        labelFormatter={(label) => `${label}`}
                      />

                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="Current Occupancy" 
                        stroke={CHART_COLORS[0]}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                       
                        dot={{ r: 4, fill: CHART_COLORS[0] }}  // Added fill color to match the line

                      />
                      <Line 
                        type="monotone" 
                        dataKey="Optimized Occupancy" 
                        stroke={CHART_COLORS[1]} 
                        strokeWidth={2}
                        dot={{ r: 4, fill: CHART_COLORS[1] }}  // Added fill color to match the line

                      />
                      <Line 
                        type="monotone" 
                        dataKey="Max Capacity" 
                        stroke="#d32f2f"
                        strokeWidth={2}
                        strokeDasharray="8 4"
                        dot={false}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper elevation={3} sx={{ p: 4 }}>
                  <Typography variant="h4" align="center" sx={{ 
                    mb: 1,
                    color: DIFC_COLORS.textPrimary,
                    fontFamily: 'Lufga',
                    fontWeight: 600 ,
                    fontSize: '1.5rem'
                    
                  }}>
                    Optimization Impact Summary
                  </Typography>
                  <Typography variant="subtitle1" align="center" sx={{ 
                    mb: 4,
                    color: DIFC_COLORS.textSecondary 
                  }}>
                    Before vs After Metrics with Visual Indicators
                  </Typography>

                  <Grid container spacing={4}>
                  {/* First KPI - Revenue Impact */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      p: 3,
                      borderRadius: 2,
                      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                      height: '100%',  // Added to make all boxes same height
                      justifyContent: 'center'  // Centers content vertically
                    }}>
                      <Box sx={{ 
                        bgcolor: '#1f2937',
                        borderRadius: '50%',
                        p: 1.5,
                        mb: 2,
                        width: '48px',  // Fixed width for circle
                        height: '48px',  // Fixed height for circle
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <QueryStatsIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Box>
                      <Typography variant="h6" sx={{ 
                        mb: 2, 
                        color: DIFC_COLORS.textPrimary,
                        fontSize: '1rem',
                        fontFamily: 'Lufga'
                      }}>
                        Revenue Impact
                      </Typography>
                      <Typography variant="h3" sx={{ 
                        mb: 1,
                        fontWeight: 600,
                        fontFamily: 'Lufga',
                        fontSize: '1.5rem',
                        textAlign: 'center'
                      }}>
                        {optimizationResults.revenue.toLocaleString()} AED
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: DIFC_COLORS.textSecondary,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Optimized Revenue Target
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Second KPI - Occupancy Gain */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      p: 3,
                      borderRadius: 2,
                      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                      height: '100%',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ 
                        bgcolor: '#1f2937',
                        borderRadius: '50%',
                        p: 1.5,
                        mb: 2,
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <QueryStatsIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Box>
                      <Typography variant="h6" sx={{ 
                        mb: 2, 
                        color: DIFC_COLORS.textPrimary,
                        fontSize: '1rem',
                        fontFamily: 'Lufga'
                      }}>
                        Occupancy Gain
                      </Typography>
                      <Typography variant="h3" sx={{ 
                        mb: 1,
                        fontWeight: 600,
                        fontFamily: 'Lufga',
                        fontSize: '1.5rem',
                        textAlign: 'center'
                      }}>
                        {optimizationResults && predictions
                          ? `${occupancyGain > 0 ? '+' : ''}${occupancyGain.toFixed(1)}%`
                          : '--'}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: DIFC_COLORS.textSecondary,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        {optimizationResults && predictions
                          ? `Average: ${(avgCurrent / getMaxCapacity() * 100).toFixed(1)}% → ${(avgOptimized / getMaxCapacity() * 100).toFixed(1)}%`
                          : 'Average: --'}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Third KPI - June Sales */}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      p: 3,
                      borderRadius: 2,
                      boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                      height: '100%',
                      justifyContent: 'center'
                    }}>
                      <Box sx={{ 
                        bgcolor: '#1f2937',
                        borderRadius: '50%',
                        p: 1.5,
                        mb: 2,
                        width: '48px',
                        height: '48px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <QueryStatsIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Box>
                      <Typography variant="h6" sx={{ 
                        mb: 2, 
                        color: DIFC_COLORS.textPrimary,
                        fontSize: '1rem',
                        fontFamily: 'Lufga'
                      }}>
                        {new Date().toLocaleString('default', { month: 'long' })} Sales
                      </Typography>
                      <Typography variant="h3" sx={{ 
                        mb: 1,
                        fontWeight: 600,
                        fontFamily: 'Lufga',
                        fontSize: '1.5rem',
                        textAlign: 'center'
                      }}>
                        {formatPieData(optimizationResults.subscription_details)
                          .reduce((sum, item) => sum + item.value, 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        color: DIFC_COLORS.textSecondary,
                        fontSize: '0.875rem',
                        textAlign: 'center'
                      }}>
                        Total Subscriptions
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {!loading && !optimizationResults && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <QueryStatsIcon sx={{ fontSize: 32, color: DIFC_COLORS.primary, mb: 1 }} />
            <Typography variant="h5" sx={{
              fontFamily: 'Lufga',
              fontWeight: 600,
              color: DIFC_COLORS.textPrimary,
              mb: 0.5,
              fontSize: '1.3rem'
            }}>
              Ready to Transform
            </Typography>
            <Typography variant="body2" sx={{
              fontFamily: 'Lufga',
              fontWeight: 300,
              color: DIFC_COLORS.textSecondary
            }}>
              Enter subscription details and click "Optimize Subscriptions"
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default SubscriptionOptimization;